import { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import UserService from '../../services/user/user.service';
import AuthService from '../../services/autentication/auth.service';
import './ReferralBanner.css';

const ReferralBanner = () => {
    const isUserLoggedIn = AuthService.isUserLoggedIn();
    const [showDialog, setShowDialog] = useState(false);
    const [referralLink, setReferralLink] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const toast = useRef<Toast>(null);

    if (!isUserLoggedIn) {
        return null;
    }

    const generateReferralLink = async () => {
        try {
            setLoading(true);
            const userDetails = await UserService.getCurrentUser();

            if (userDetails && userDetails.userID) {
                const code = btoa(`REF_${userDetails.userID}`);
                setReferralCode(code);

                // Use public URL from env or fallback to origin
                const baseUrl = (process.env.REACT_APP_URL || window.location.origin).replace(/\/$/, '');
                const link = `${baseUrl}/register?ref=${code}`;
                setReferralLink(link);
            }
        } catch (error) {
            console.error('Error generating referral link:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setShowDialog(true);
        if (!referralLink) {
            generateReferralLink();
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true);
            toast.current?.show({
                severity: 'success',
                summary: 'Copied!',
                detail: 'Referral link copied to clipboard',
                life: 3000
            });
            setTimeout(() => setCopied(false), 3000);
        });
    };

    const shareViaWhatsApp = () => {
        const message = encodeURIComponent(
            `🎁 Join Farm2Me and let's both earn rewards!\n\n` +
            `I'll earn exclusive rewards on EVERY purchase you make for the next 365 days! 🌟\n\n` +
            `Use my privilege link to sign up:\n\n` +
            `${referralLink}\n\n` +
            `It's a win-win! Experience premium quality with Farm2Me! 💚`
        );
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    const shareViaEmail = () => {
        const subject = encodeURIComponent('Join Farm2Me - Earn Rewards Together!');
        const body = encodeURIComponent(
            `Hi!\n\nI'd like to invite you to join Farm2Me. When you use my link to sign up, we BOTH earn rewards!\n\nLink: ${referralLink}`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <>
            <Toast ref={toast} />

            {/* Compact Banner for Home Page */}
            <div className="referral-banner" onClick={handleOpenDialog}>
                <div className="referral-banner-content">
                    <div className="referral-banner-icon">
                        <i className="pi pi-gift"></i>
                    </div>
                    <div className="referral-banner-text">
                        <h3>Refer & Earn !</h3>
                        <p>Earn rewards on every order your friends make</p>
                    </div>
                    <div className="referral-banner-action">
                        <Button
                            label="Get Link"
                            icon="pi pi-share-alt"
                            className="p-button-success"
                        />
                    </div>
                </div>
            </div>

            {/* Full Details Dialog */}
            <Dialog
                visible={showDialog}
                style={{ width: '95vw', maxWidth: '440px', overflowY: 'visible' }}
                onHide={() => setShowDialog(false)}
                dismissableMask
                closable={false}
                contentStyle={{ borderRadius: '16px', padding: 0, overflow: 'hidden', background: 'transparent' }}
                showHeader={false}
            >
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#059669' }}></i>
                        <p style={{ marginTop: '1rem', color: '#64748b', fontWeight: '500' }}>Getting your link ready...</p>
                    </div>
                ) : (
                    <div style={{ position: 'relative', overflow: 'hidden', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', color: '#1e293b', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        {/* Background accents */}
                        <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>

                        {/* Close Button */}
                        <button
                            onClick={() => setShowDialog(false)}
                            style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.9rem', zIndex: 10, transition: 'all 0.2s' }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
                        >
                            <i className="pi pi-times"></i>
                        </button>

                        <div style={{ padding: '2rem 1.5rem 0.5rem', position: 'relative', zIndex: 5, textAlign: 'center' }}>
                            {/* Premium Green Icon */}
                            <div style={{
                                width: '48px',
                                height: '48px',
                                margin: '0 auto 0.75rem',
                                background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 20px rgba(5, 150, 105, 0.25)',
                                transform: 'rotate(-5deg)'
                            }}>
                                <i className="pi pi-gift" style={{ fontSize: '1.4rem', color: '#ffffff', transform: 'rotate(5deg)' }}></i>
                            </div>

                            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', margin: '0 0 0.5rem', letterSpacing: '-0.02em', color: '#0f172a', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                                Share The <span style={{ color: '#059669' }}>Rewards</span>
                            </h2>

                            <p style={{ color: '#475569', fontSize: '0.85rem', margin: '0', lineHeight: '1.4', fontWeight: '400', padding: '0 0.5rem' }}>
                                Invite friends to Farm2Me. You'll earn exclusive rewards on <i>every purchase</i> they make for the <strong style={{ color: '#047857', fontWeight: '700' }}>next 365 days</strong> from the day they join.
                            </p>
                        </div>

                        {/* How it Works Section */}
                        <div style={{ padding: '0 1.5rem', position: 'relative', zIndex: 5, marginBottom: '0.75rem' }}>
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                <p style={{ fontSize: '0.65rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0' }}>How It Works</p>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#d1fae5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', flexShrink: 0 }}>1</div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#334155', lineHeight: '1.2' }}><strong>Share your link</strong> with your friends</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#d1fae5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', flexShrink: 0 }}>2</div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#334155', lineHeight: '1.2' }}><strong>They register & shop</strong> using your invite</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#d1fae5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', flexShrink: 0 }}>3</div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#334155', lineHeight: '1.2' }}><strong>You earn rewards</strong> on EVERY purchase they make for 365 Days!</p>
                                </div>
                            </div>
                        </div>

                        {/* Link Section */}
                        <div style={{ padding: '0 1.5rem', position: 'relative', zIndex: 5 }}>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '12px',
                                padding: '0.6rem 0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.8rem'
                            }}>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <p style={{ fontSize: '0.65rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.2rem' }}>Your Unique Link</p>
                                    <p style={{ margin: 0, color: '#0f172a', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {referralLink}
                                    </p>
                                </div>
                                <button
                                    onClick={copyToClipboard}
                                    style={{
                                        background: copied ? '#059669' : '#ffffff',
                                        color: copied ? '#ffffff' : '#059669',
                                        border: copied ? '1px solid #059669' : '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        flexShrink: 0,
                                        boxShadow: copied ? '0 4px 10px rgba(5, 150, 105, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <i className={copied ? "pi pi-check" : "pi pi-copy"} style={{ fontSize: '0.9rem' }}></i>
                                </button>
                            </div>
                        </div>

                        {/* Action Area */}
                        <div style={{ padding: '1rem 1.5rem 1rem', position: 'relative', zIndex: 5, marginTop: 'auto' }}>
                            <Button
                                onClick={shareViaWhatsApp}
                                className="w-full"
                                style={{
                                    background: '#25D366',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '0.8rem',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    letterSpacing: '0.02em',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    transition: 'all 0.2s',
                                    color: '#ffffff',
                                    boxShadow: '0 8px 15px -5px rgba(37, 211, 102, 0.3)'
                                }}
                            >
                                <i className="pi pi-whatsapp" style={{ fontSize: '1.1rem' }}></i>
                                <span>Share via WhatsApp</span>
                            </Button>
                        </div>

                        {/* Premium Footer */}
                        <div style={{ background: '#f1f5f9', padding: '0.75rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
                            <i className="pi pi-star-fill" style={{ color: '#059669', fontSize: '0.75rem' }}></i>
                            <p style={{ margin: 0, fontSize: '0.7rem', color: '#475569', fontWeight: '600' }}>365 Days of Guaranteed Rewards</p>
                            <i className="pi pi-star-fill" style={{ color: '#059669', fontSize: '0.75rem' }}></i>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default ReferralBanner;

